<script setup>
const { t } = useI18n();

defineProps({
  data: { type: Object, required: true },
  // When a section holds a single event, it renders wide (image + text
  // side by side) instead of as a lone card stranded in a multi-column grid.
  featured: { type: Boolean, required: false },
});
</script>

<template>
  <!-- Featured: wide, horizontal -->
  <article
    v-if="featured"
    class="card group grid overflow-hidden p-0 md:grid-cols-2"
  >
    <div v-if="data.image" class="relative aspect-[16/10] overflow-hidden md:aspect-auto md:min-h-[360px]">
      <img
        :src="data.image.url"
        :alt="data.image.alt_text || data.title"
        loading="lazy"
        class="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div aria-hidden="true" class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
    </div>

    <div class="flex flex-col justify-center gap-4 p-8 md:p-10">
      <div class="text-sm font-semibold uppercase tracking-wide text-brand-orange">{{ data.date }}</div>
      <h3 class="font-display text-2xl md:text-3xl font-bold leading-snug transition-colors group-hover:text-brand-green">{{ data.title }}</h3>
      <p v-if="data.location" class="text-sm text-muted flex items-center gap-1.5">
        <svg class="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M8 14s5-4 5-8a5 5 0 10-10 0c0 4 5 8 5 8z" stroke="currentColor" stroke-width="1.4" />
          <circle cx="8" cy="6" r="1.6" stroke="currentColor" stroke-width="1.4" />
        </svg>
        {{ data.location }}
      </p>
      <p v-if="data.club" class="text-sm text-muted flex items-center gap-1.5">
        <svg class="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="5.5" cy="5" r="2" stroke="currentColor" stroke-width="1.4" />
          <path d="M1.75 13.25c.4-2.4 1.95-3.75 3.75-3.75s3.35 1.35 3.75 3.75" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
          <path d="M10.5 7a2 2 0 100-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
          <path d="M11.25 9.6c1.55.35 2.6 1.6 2.95 3.65" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
        </svg>
        {{ data.club }}
      </p>
      <p v-if="data.description" class="text-ink/80 leading-relaxed">
        {{ data.description }}
      </p>
      <span class="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-green">
        {{ t("common.readMore") }}
        <svg class="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
    </div>
  </article>

  <!-- Default: compact, vertical (grid cell) -->
  <article v-else class="card group flex h-full flex-col overflow-hidden p-0">
    <div v-if="data.image" class="relative aspect-[3/2] overflow-hidden">
      <img
        :src="data.image.url"
        :alt="data.image.alt_text || data.title"
        loading="lazy"
        class="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div aria-hidden="true" class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
    </div>

    <div class="flex flex-1 flex-col p-6">
      <div class="space-y-2.5">
        <div class="text-sm font-semibold tracking-wide text-brand-orange">{{ data.date }}</div>
        <h3 class="font-display text-xl font-bold leading-snug transition-colors group-hover:text-brand-green">{{ data.title }}</h3>
        <p v-if="data.location" class="text-sm text-muted flex items-center gap-1.5">
          <svg class="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 14s5-4 5-8a5 5 0 10-10 0c0 4 5 8 5 8z" stroke="currentColor" stroke-width="1.4" />
            <circle cx="8" cy="6" r="1.6" stroke="currentColor" stroke-width="1.4" />
          </svg>
          {{ data.location }}
        </p>
        <p v-if="data.club" class="text-sm text-muted flex items-center gap-1.5">
          <svg class="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="5.5" cy="5" r="2" stroke="currentColor" stroke-width="1.4" />
            <path d="M1.75 13.25c.4-2.4 1.95-3.75 3.75-3.75s3.35 1.35 3.75 3.75" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
            <path d="M10.5 7a2 2 0 100-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
            <path d="M11.25 9.6c1.55.35 2.6 1.6 2.95 3.65" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
          </svg>
          {{ data.club }}
        </p>
        <p v-if="data.description" class="text-sm text-ink/80 line-clamp-3 leading-relaxed">
          {{ data.description }}
        </p>
      </div>
      <span class="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-semibold text-brand-green">
        {{ t("common.readMore") }}
        <svg class="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
    </div>
  </article>
</template>
